import React, { useCallback, useEffect, useState } from "react";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";
import GroupAddOutlinedIcon from "@mui/icons-material/GroupAddOutlined";
import { useDispatch, useSelector } from "react-redux";
import api, { setAuthToken } from "utils/api";
import { queueAuthSuccessPopup } from "utils/authSuccessPopup";
import { setLogout, setUser } from "state";
import TeamShell from "scenes/team/TeamShell";
import { TeamLocaleProvider } from "scenes/team/TeamLocaleContext";
import DealMapTab from "scenes/team/DealMapTab";
import hoffmannLogo from "assets/images/Dr_Hoffmann_Logo_f1.svg";
import revenueImage from "assets/images/dicaprio_umsatz.jpeg";
import calendarImage from "assets/images/dicaprio_terminkalender.webp";
import referralsImage from "assets/images/dicaprio_empfehlungen.jpg";
import EmployeeLogin from "./EmployeeLogin";
import EmployeeCalendarTab from "./EmployeeCalendarTab";
import UmsatzTab from "./UmsatzTab";
import ReferralTab from "./ReferralTab";
import "styles/booking.css";
import "styles/team.css";
import "styles/employee.css";

const isEmployeeUser = (user) =>
  user?.isEmployee === true || user?.isEmployee === "true";

const EMPLOYEE_TABS = [
  { id: "dashboard", label: "Dashboard" },
  { id: "calendar", label: "Terminkalender" },
  { id: "umsatz", label: "Umsatz" },
  { id: "referrals", label: "Empfehlungen" },
  { id: "dealmap", label: "CRM-Karte" },
];

const HOME_SHORTCUTS = [
  { tab: "calendar", title: "Terminkalender", icon: <EventOutlinedIcon />, image: calendarImage, imagePosition: "center 35%" },
  { tab: "umsatz", title: "Umsatz", icon: <TrendingUpOutlinedIcon />, image: revenueImage, imagePosition: "center top" },
  { tab: "referrals", title: "Empfehlungen", icon: <GroupAddOutlinedIcon />, image: referralsImage, imagePosition: "center 30%" },
];

const EmployeeHome = ({ user, onNavigate }) => {
  const name = user?.firstName?.trim() || user?.email?.split("@")[0] || "Team";

  return (
    <div className="employee-home" role="tabpanel" aria-label="Dashboard">
      <section className="employee-home-hero">
        <div className="employee-home-hero-content">
          <p className="employee-home-eyebrow">Dein Dr. Hoffmann Arbeitsbereich</p>
          <h1>Willkommen zurück, <em>{name}!</em></h1>
          <p className="employee-home-intro">
            Schön, dass du da bist. Bist du bereit für deine Tour?
          </p>
          <div className="employee-home-tour-row">
            <button
              type="button"
              className="employee-home-tour"
              onClick={() => onNavigate("dealmap")}
            >
              Tour starten <span aria-hidden="true">↗</span>
            </button>
          </div>
        </div>
        <div className="employee-home-logo-halo">
          <img className="employee-home-logo" src={hoffmannLogo} alt="Dr. Hoffmann Logo" />
        </div>
      </section>

      <section className="employee-home-shortcuts" aria-label="Schnellzugriff">
        <div className="employee-home-shortcuts-heading">
          <p className="employee-kicker">Alles im Blick</p>
          <h2>Dein Arbeitsbereich</h2>
        </div>
        <div className="employee-home-shortcut-grid">
          {HOME_SHORTCUTS.map((item) => (
            <button
              key={item.tab}
              type="button"
              className="employee-home-shortcut"
              onClick={() => onNavigate(item.tab)}
            >
              <img
                className="employee-home-shortcut-image"
                src={item.image}
                alt=""
                loading="lazy"
                decoding="async"
                style={{ objectPosition: item.imagePosition }}
              />
              <span className="employee-home-shortcut-icon" aria-hidden="true">{item.icon}</span>
              <span className="employee-home-shortcut-title">{item.title}</span>
              <span className="employee-home-shortcut-description">{item.description}</span>
              <span className="employee-home-shortcut-arrow" aria-hidden="true">↗</span>
            </button>
          ))}
        </div>
      </section>
    </div>
  );
};

const EmployeeDashboard = ({ user, onLogout }) => {
  const [activeTab, setActiveTab] = useState("dashboard");
  const [navOpen, setNavOpen] = useState(false);

  return (
    <section className="employee-page-inner">
      <header className="employee-topbar" aria-label="Mitarbeiter-Navigation">
        <div className="employee-topbar-main">
          <button
            type="button"
            className="employee-topbar-brand"
            aria-label="Zum Mitarbeiter-Dashboard"
            onClick={() => {
              setActiveTab("dashboard");
              setNavOpen(false);
            }}
          >
            <img src={hoffmannLogo} alt="Dr. Hoffmann" />
          </button>
          <div className="employee-topbar-profile">
            <span className="employee-topbar-user">
              {user.firstName ? `${user.firstName} ${user.lastName || ""}`.trim() : user.email}
            </span>
            <button type="button" className="employee-logout" onClick={onLogout}>
              Abmelden
            </button>
          </div>
        </div>
        <button
          type="button"
          className={`employee-nav-toggle${navOpen ? " is-open" : ""}`}
          aria-label={navOpen ? "Navigation schließen" : "Navigation öffnen"}
          aria-expanded={navOpen}
          aria-controls="employee-navigation"
          onClick={() => setNavOpen((open) => !open)}
        >
          <span className="employee-nav-chevrons" aria-hidden="true">
            <span />
            <span />
          </span>
        </button>
        <div
          id="employee-navigation"
          role="navigation"
          className={`employee-nav-panel${navOpen ? " is-open" : ""}`}
          aria-label="Mitarbeiter-Bereiche"
          aria-hidden={!navOpen}
        >
          <div className="employee-nav-panel-inner">
            <div className="employee-tabs" role="tablist" aria-label="Mitarbeiter-Bereiche">
              {EMPLOYEE_TABS.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={activeTab === tab.id}
                  className={activeTab === tab.id ? "is-active" : ""}
                  onClick={() => {
                    setActiveTab(tab.id);
                    setNavOpen(false);
                  }}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </header>

      <div className="team-page-inner">
        {activeTab === "dashboard" && <EmployeeHome user={user} onNavigate={setActiveTab} />}
        {activeTab === "calendar" && <EmployeeCalendarTab embedded />}
        {activeTab === "umsatz" && <UmsatzTab />}
        {activeTab === "referrals" && <ReferralTab user={user} />}
        {activeTab === "dealmap" && (
          <div className="team-panel employee-dealmap-panel">
            <DealMapTab apiBase="/employee/hubspot/deal-pins" />
          </div>
        )}
      </div>
    </section>
  );
};

const EmployeePageContent = () => {
  const token = useSelector((s) => s.token);
  const user = useSelector((s) => s.user);
  const dispatch = useDispatch();
  const [sessionReady, setSessionReady] = useState(!token);
  const [loginKey, setLoginKey] = useState(0);

  const refreshSession = useCallback(async () => {
    if (!token) {
      setAuthToken(null);
      setSessionReady(true);
      return;
    }
    setAuthToken(token);
    setSessionReady(false);
    try {
      const { data } = await api.get("/auth/me");
      dispatch(setUser({ user: data.user }));
    } catch {
      setAuthToken(null);
      dispatch(setLogout());
    } finally {
      setSessionReady(true);
    }
  }, [token, dispatch]);

  useEffect(() => {
    refreshSession();
  }, [refreshSession]);

  const handleLogout = () => {
    setAuthToken(null);
    dispatch(setLogout());
    queueAuthSuccessPopup("logout");
    setLoginKey((k) => k + 1);
  };

  if (!sessionReady) {
    return (
      <TeamShell>
        <div className="employee-empty">Lade…</div>
      </TeamShell>
    );
  }

  if (!token) {
    return (
      <TeamShell>
        <EmployeeLogin key={loginKey} onSuccess={refreshSession} />
      </TeamShell>
    );
  }

  if (!isEmployeeUser(user) || user?.isAdmin) {
    return (
      <TeamShell>
        <section className="team-login">
          <div className="auth-card team-login-card">
            <h1>Kein Zugriff</h1>
            <p className="auth-lead">
              Dieser Bereich ist nur für Mitarbeiter-Konten verfügbar.
            </p>
            <div className="auth-actions">
              <button type="button" className="auth-submit" onClick={handleLogout}>
                Abmelden
              </button>
            </div>
          </div>
        </section>
      </TeamShell>
    );
  }

  return (
    <TeamShell>
      <EmployeeDashboard user={user} onLogout={handleLogout} />
    </TeamShell>
  );
};

const EmployeePage = () => (
  <TeamLocaleProvider>
    <EmployeePageContent />
  </TeamLocaleProvider>
);

export default EmployeePage;
